import { MenuItem } from '@headlessui/react';
import Icon, { IconKey } from 'flow/components/ui-components/Icon';
import React, {
    ReactNode,
    useContext,
    useEffect,
    useId,
    useMemo,
    useRef,
    useState,
} from 'react';
import { usePopper } from 'react-popper';
import { Placement } from '@popperjs/core';
import { twMerge } from 'tailwind-merge';

// Keep the submenu open briefly after the pointer leaves so it survives
// crossing the gap between the parent row and the submenu panel (or a small
// drift off the panel while reading/typing). Re-entering cancels the close.
const CLOSE_DELAY_MS = 250;

// Coordinates sibling submenus so that only one is open at a time. Without
// this, moving from one submenu row to a sibling leaves the first panel
// lingering for CLOSE_DELAY_MS, so both panels briefly render stacked on top
// of each other. Opening a submenu closes the previously open sibling
// immediately (bypassing the close delay).
type NestedMenuGroup = {
    activate: (id: string, closeNow: () => void) => void;
    deactivate: (id: string) => void;
};

const createNestedMenuGroup = (): NestedMenuGroup => {
    let active: { id: string; closeNow: () => void } | null = null;
    return {
        activate: (id, closeNow) => {
            if (active && active.id !== id) {
                active.closeNow();
            }
            active = { id, closeNow };
        },
        deactivate: (id) => {
            if (active && active.id === id) {
                active = null;
            }
        },
    };
};

// The default group is shared by every top-level NestedMenuItem that isn't
// wrapped in a nested group — i.e. the direct submenu rows of a context menu.
// Each NestedMenuItem provides a fresh group to its own children, so a nested
// submenu opening never closes its parent.
const NestedMenuGroupContext = React.createContext<NestedMenuGroup>(
    createNestedMenuGroup(),
);

interface Props {
    children: ReactNode;
    iconKey?: IconKey;
    title?: ReactNode | string;
    placementDesired?: Placement;
    offset?: [number, number];
    className?: string;
    /** Optional fixed content rendered above the scrollable list (e.g. a search field). */
    header?: ReactNode;
    /** Extra classes for the scrollable list container (e.g. to override max-height). */
    listClassName?: string;
    /** Called when the submenu opens (true) or closes (false), e.g. to reset header state. */
    onOpenChange?: (open: boolean) => void;
    onClick?: () => void;
}

const NestedMenuItem = ({
    children,
    iconKey,
    title,
    placementDesired = 'right',
    offset = [0, 0],
    className,
    header,
    listClassName,
    onOpenChange,
    onClick,
}: Props) => {
    const [referenceElement, setReferenceElement] = useState(null);
    const [popperElement, setPopperElement] = useState(null);
    const [isMenuOpen, setIsMenuOpen] = useState(false);
    const closeTimeoutRef = useRef<ReturnType<typeof setTimeout>>();

    const id = useId();
    // The group of the parent menu this row belongs to (siblings coordinate).
    const group = useContext(NestedMenuGroupContext);
    // A fresh group for this submenu's own children, kept stable across renders.
    const childGroup = useMemo(() => createNestedMenuGroup(), []);

    const cancelScheduledClose = () => {
        if (closeTimeoutRef.current) {
            clearTimeout(closeTimeoutRef.current);
            closeTimeoutRef.current = undefined;
        }
    };

    // Close without the grace delay. Used both when leaving the menu entirely
    // and when a sibling submenu forces this one shut via the group.
    const closeNow = () => {
        cancelScheduledClose();
        setIsMenuOpen(false);
        onOpenChange?.(false);
    };

    const openMenu = () => {
        cancelScheduledClose();
        // Closes any sibling that is still open, so the panels never stack.
        group.activate(id, closeNow);
        setIsMenuOpen(true);
        onOpenChange?.(true);
    };

    const scheduleClose = () => {
        cancelScheduledClose();
        closeTimeoutRef.current = setTimeout(() => {
            setIsMenuOpen(false);
            onOpenChange?.(false);
            group.deactivate(id);
        }, CLOSE_DELAY_MS);
    };

    // Clear any pending close timer and drop out of the group on unmount, to
    // avoid setting state or closing a stale reference after the component is
    // gone.
    useEffect(
        () => () => {
            cancelScheduledClose();
            group.deactivate(id);
        },
        [group, id],
    );

    const placement =
        getPlacementLeftRightFlippedIfDocumentIsRtl(placementDesired);
    const { styles, attributes } = usePopper(referenceElement, popperElement, {
        placement: placement,
        modifiers: [
            {
                name: 'offset',
                options: {
                    offset,
                },
            },
            {
                name: 'flip',
                enabled: true,
                options: {
                    flipVariations: true,
                    allowedAutoPlacements: [
                        'auto',
                        'auto-start',
                        'auto-end',
                        'top',
                        'top-start',
                        'top-end',
                        'bottom',
                        'bottom-start',
                        'bottom-end',
                        'right',
                        'right-start',
                        'right-end',
                        'left',
                        'left-start',
                        'left-end',
                    ],
                },
            },
            { name: 'preventOverflow', enabled: true },
        ],
    });

    return (
        <MenuItem
            as={'div'}
            onClick={onClick}
            ref={setReferenceElement}
            className="group flex flex-1 cursor-pointer items-center rounded-sm hover:bg-gray-200"
            onMouseEnter={openMenu}
            onMouseLeave={scheduleClose}
        >
            <div className="flex flex-1 cursor-pointer items-center rounded-sm p-2 hover:bg-gray-200 dark:hover:bg-gray-300">
                {iconKey && (
                    <Icon
                        iconKey={iconKey}
                        className="me-2 text-gray-600"
                        size={'24px'}
                    />
                )}
                <span className="flex-1">{title}</span>

                <div className="arrow">
                    <Icon
                        iconKey="chevron_right"
                        className="text-gray-600 rtl:scale-x-[-1]"
                        size={'24px'}
                    />
                </div>

                {isMenuOpen && (
                    <div
                        ref={setPopperElement}
                        style={styles.popper}
                        {...attributes.popper}
                    >
                        <div
                            className={twMerge(
                                'shadow-base end-2 z-50 mx-3 min-w-[200px] rounded-sm bg-white p-2 dark:bg-gray-200',
                                className,
                            )}
                        >
                            {header}
                            <div
                                className={twMerge(
                                    'max-h-[270px] overflow-y-auto',
                                    listClassName,
                                )}
                            >
                                <NestedMenuGroupContext.Provider
                                    value={childGroup}
                                >
                                    {children}
                                </NestedMenuGroupContext.Provider>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </MenuItem>
    );
};

const getPlacementLeftRightFlippedIfDocumentIsRtl = (placement: Placement) => {
    if (document.dir !== 'rtl') {
        //Document is not right to left, return placement as is
        return placement;
    }

    //Document direction is Right to left, reverse if placement is of a left/right type

    if (placement === 'right') return 'left';
    if (placement === 'right-start') return 'left-start';
    if (placement === 'right-end') return 'left-end';

    if (placement === 'left') return 'right';
    if (placement === 'left-start') return 'right-start';
    if (placement === 'left-end') return 'right-end';

    return placement;
};

export default NestedMenuItem;
